import { Card, Col, Row, Statistic, Typography, Progress, Tag, Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import { LevelCOverallStatistics } from "../../types/levelc-reports";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  TrophyOutlined,
  HourglassOutlined,
  PercentageOutlined,
} from "@ant-design/icons";

interface LevelCOverallStatsProps {
  statistics: LevelCOverallStatistics | undefined;
  isLoading: boolean;
}

export const LevelCOverallStats = ({
  statistics,
  isLoading,
}: LevelCOverallStatsProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[1, 2, 3, 4].map((i) => (
          <Col xs={24} sm={12} lg={6} key={i}>
            <Card>
              <Skeleton active paragraph={false} />
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  if (!statistics) {
    return null;
  }

  const passRateColor =
    statistics.passRate < 50
      ? "#ff4d4f"
      : statistics.passRate <= 70
      ? "#faad14"
      : "#52c41a";

  return (
    <>
      {/* Main KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title={
                <Typography.Text type="secondary">
                  {t("levelCReports.stats.totalCandidates")}
                </Typography.Text>
              }
              value={statistics.totalCandidates}
              prefix={<TeamOutlined style={{ color: "#1890ff" }} />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title={
                <Typography.Text type="secondary">
                  {t("levelCReports.stats.passRate")}
                </Typography.Text>
              }
              value={statistics.passRate}
              precision={1}
              suffix="%"
              prefix={<PercentageOutlined style={{ color: passRateColor }} />}
              valueStyle={{ color: passRateColor }}
            />
            <Progress
              percent={statistics.passRate}
              size="small"
              showInfo={false}
              strokeColor={passRateColor}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title={
                <Typography.Text type="secondary">
                  {t("levelCReports.stats.passed")}
                </Typography.Text>
              }
              value={statistics.passedCandidates}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title={
                <Typography.Text type="secondary">
                  {t("levelCReports.stats.failed")}
                </Typography.Text>
              }
              value={statistics.failedCandidates}
              prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Secondary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card size="small">
            <Statistic
              title={t("levelCReports.stats.averageScore")}
              value={statistics.averageScore}
              precision={1}
              prefix={<TrophyOutlined style={{ color: "#faad14" }} />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card size="small">
            <Statistic
              title={t("levelCReports.stats.gradingComplete")}
              value={statistics.gradingComplete}
              suffix={
                <Tag color="success" style={{ marginLeft: 8 }}>
                  {t("levelCReports.gradingStatus.complete")}
                </Tag>
              }
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card size="small">
            <Statistic
              title={t("levelCReports.stats.gradingInProgress")}
              value={statistics.gradingInProgress}
              prefix={<HourglassOutlined style={{ color: "#1890ff" }} />}
              suffix={
                statistics.gradingInProgress > 0 && (
                  <Tag color="processing" style={{ marginLeft: 8 }}>
                    {t("levelCReports.gradingStatus.inProgress")}
                  </Tag>
                )
              }
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

